import React from 'react';
import { Button } from '../../components/ui/button';
import { CheckCircle, Download, AlertTriangle, FileText, Shield, Wallet, Coins, ArrowRight, Lock, Loader2, Send } from 'lucide-react';
import EchelonnementPreview from '../../components/EchelonnementPreview';
import { getDashboardRouteForRole } from '../../utils/roleNavigation';
import EcheancierMasqueToggle, { useEcheancierMasque } from '../../components/EcheancierMasqueToggle';
import BandeauAvenant from './BandeauAvenant';

export default function ResultatsActions({
  resultat, modeCalcul, modeSimulateur, user, formData,
  contratParent, walletBalance, creditCostPdf, hasEnoughCredits, loadingDevis,
  handleDownloadDevisSimulation, handleSouscrire, handleCreerAvenant, propositionConversion, handleSouscrireFromProposition,
  dismissPropositionConversion, isFtpeMember, loading, selectedActivitesPIB = [], selectedActivitesGO = [],
  selectedActivites2nd = [], selectedActivitesTP = [], montantsPersonnalises, setMontantsPersonnalises, getMontantsFinals,
  getTaxesFinals, taxesPersonnalisees, setTaxesPersonnalisees, isSuperAdmin, isAdminOrSuperAdmin,
  validitePersonnalisee, setValiditePersonnalisee, updateLigneTaxe, supprimerLigneTaxe, ajouterLigneTaxe,
  remiseSolidaireNegociee = null, onEcheancierPlanChange = null, signatureModalOpen, setSignatureModalOpen}) {
  const [echeancierCache] = useEcheancierMasque();
  return (
    <>
                    {modeSimulateur === 'avenant' && contratParent && (
                      <BandeauAvenant contratParent={contratParent} resultat={resultat} isSuperAdmin={isSuperAdmin} />
                    )}
                    {user && (
                      <div className="border-t pt-4 mt-4">
                        {/* Affichage du solde wallet si disponible - masqué pour admin/super_admin */}
                        {!isAdminOrSuperAdmin && walletBalance !== null && (
                          <div className={`flex items-center justify-between mb-3 p-2 rounded-lg border ${
                            hasEnoughCredits 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              <Wallet className={`h-4 w-4 ${hasEnoughCredits ? 'text-emerald-600' : 'text-red-600'}`} />
                              <span className={`text-sm ${hasEnoughCredits ? 'text-emerald-700' : 'text-red-700'}`}>
                                Solde: <strong>{walletBalance}</strong> crédits
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <Coins className="h-4 w-4 text-blue-600" />
                              <span className="text-blue-700">Coût: <strong>{creditCostPdf}</strong> crédits</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Badge Admin/Super Admin - accès illimité */}
                        {isAdminOrSuperAdmin && (
                          <div className="flex items-center justify-center mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                            <Shield className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-sm text-purple-700 font-medium">Accès administrateur - Pas de crédits requis</span>
                          </div>
                        )}
                        
                        {/* Alerte solde insuffisant avec bouton de recharge - masqué pour admin/super_admin */}
                        {!isAdminOrSuperAdmin && walletBalance !== null && walletBalance < creditCostPdf && (
                          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">
                                  Crédits insuffisants
                                </p>
                                <p className="text-xs text-amber-700 mt-1">
                                  Il vous manque <strong>{creditCostPdf - walletBalance}</strong> crédit(s) pour télécharger la proposition ESS.
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                                  onClick={() => {
                                    // V36.85 — Route role-aware (un super_admin
                                    // ne doit pas être renvoyé sur /cooper/dashboard)
                                    const base = getDashboardRouteForRole(user?.role);
                                    window.location.href = `${base}?tab=wallet-credits`;
                                  }}
                                  data-testid="recharge-wallet-btn"
                                >
                                  <Wallet className="mr-1 h-3 w-3" />
                                  Recharger mon wallet
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <Button 
                          onClick={handleDownloadDevisSimulation}
                          disabled={loadingDevis || (!isAdminOrSuperAdmin && walletBalance !== null && walletBalance < creditCostPdf)}
                          variant="outline"
                          className={`w-full h-11 border-2 font-semibold ${
                            !isAdminOrSuperAdmin && walletBalance !== null && walletBalance < creditCostPdf
                              ? 'border-slate-300 text-slate-400 bg-slate-50 cursor-not-allowed'
                              : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                          }`}
                          data-testid="download-devis-btn"
                          data-version="20260119-v3"
                        >
                          {loadingDevis ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Génération du devis...
                            </>
                          ) : !isAdminOrSuperAdmin && walletBalance !== null && walletBalance < creditCostPdf ? (
                            <>
                              <Lock className="mr-2 h-5 w-5" />
                              Crédits insuffisants
                            </>
                          ) : (
                            <>
                              <Download className="mr-2 h-5 w-5" />
                              Télécharger la Proposition ESS PDF
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-center mt-2 text-slate-500">
                          {!isAdminOrSuperAdmin && walletBalance !== null && walletBalance < creditCostPdf 
                            ? 'Rechargez votre wallet pour débloquer le téléchargement'
                            : 'Téléchargez un récapitulatif PDF de cette simulation'
                          }
                        </p>
                      </div>
                    )}

                    {/* V36.5/V36.9 — Bloc Paiement échelonné (cohérent avec PDF Proposition ESS).
                        Visible si TTC > seuil (2500€ par défaut, 1000€ en nominative).
                        Super Admin peut personnaliser via la modale. */}
                    <EcheancierMasqueToggle />
                    {!echeancierCache && (resultat?.montant_ttc || (montantsPersonnalises.enabled && getMontantsFinals()?.montant_ttc)) && (
                      <EchelonnementPreview
                        montantTotalEur={
                          (montantsPersonnalises.enabled && getMontantsFinals()?.montant_ttc) ||
                          resultat?.montant_ttc || 0
                        }
                        context={modeCalcul === 'nominative' ? 'nominative' : 'default'}
                        onPlanChange={onEcheancierPlanChange}
                      />
                    )}

                    {/* ========================================================
                        BANNIÈRE DE CONVERSION — Proposition → Souscription 1 clic
                        Apparaît après téléchargement réussi, persiste jusqu'à
                        nouvelle simulation ou fermeture explicite par le user.
                        Pré-remplit le workflow cible (auto-détection rôle) avec
                        le snapshot complet (sessionStorage + location.state).
                       ======================================================== */}
                    {propositionConversion && user && (
                      <div
                        className="mt-4 relative overflow-hidden rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-5 shadow-md"
                        data-testid="proposition-conversion-banner"
                      >
                        {/* Bouton fermer (croix) */}
                        <button
                          type="button"
                          onClick={dismissPropositionConversion}
                          className="absolute top-2 right-2 p-1.5 rounded-full text-slate-400 hover:bg-slate-200/60 hover:text-slate-700 transition-colors"
                          aria-label="Masquer la bannière"
                          data-testid="proposition-conversion-dismiss"
                        >
                          <span className="block w-4 h-4 leading-none text-lg font-light">×</span>
                        </button>

                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">
                            <div className="relative">
                              <div className="absolute inset-0 bg-emerald-500/30 rounded-xl blur-md animate-pulse"></div>
                              <div className="relative p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                                <CheckCircle className="h-5 w-5 text-white" />
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white">
                                Proposition prête
                              </span>
                              <span className="text-xs font-mono text-slate-500" data-testid="proposition-conversion-devis-id">
                                #{propositionConversion.devisId}
                              </span>
                            </div>
                            <h4 className="text-base font-bold text-slate-900 mb-1">
                              Cette proposition est valable jusqu'au{' '}
                              <span className="text-emerald-700" data-testid="proposition-conversion-validite">
                                {propositionConversion.dateValidite}
                              </span>
                              {' '}({propositionConversion.validiteJours} jour{propositionConversion.validiteJours > 1 ? 's' : ''})
                            </h4>
                            <p className="text-sm text-slate-600 mb-3">
                              Souscrivez en 1 clic — toutes vos données (membre, activités,
                              montants, taxes) sont déjà pré-remplies. Aucune ressaisie.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                onClick={handleSouscrireFromProposition}
                                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-md h-10 flex-1 sm:flex-none sm:px-6"
                                data-testid="proposition-conversion-cta"
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Souscrire en 1 clic
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setSignatureModalOpen(true)}
                                className="border-purple-300 bg-white text-purple-700 hover:bg-purple-50 hover:border-purple-400 font-semibold h-10 sm:flex-none sm:px-4"
                                data-testid="proposition-send-signature-btn"
                              >
                                <Send className="mr-2 h-4 w-4" />
                                Envoyer pour signature
                              </Button>
                              <Button
                                variant="outline"
                                onClick={dismissPropositionConversion}
                                className="border-slate-300 text-slate-600 hover:bg-slate-100 h-10 sm:flex-none sm:px-4"
                                data-testid="proposition-conversion-later"
                              >
                                Plus tard
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Boutons de souscription */}
                    {/* Mode AVENANT - Bouton d'acceptation */}
                    {modeSimulateur === 'avenant' && contratParent && user && (user.role === 'cooper' || user.role === 'super_admin') && (
                      <div className="border-t pt-4 mt-4 space-y-3">
                        <Button 
                          onClick={handleCreerAvenant}
                          disabled={loading}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-12 text-base font-semibold shadow-lg"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Création en cours...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-2 h-5 w-5" />
                              J'accepte la proposition et poursuis l'adhésion
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-center text-slate-500">
                          En cliquant, vous acceptez les conditions et serez redirigé vers la finalisation de l'avenant
                        </p>
                      </div>
                    )}
                    
                    {/* Mode NOMINATIVE - Pour les membres */}
                    {modeCalcul === 'nominative' && user && user.role === 'member' && modeSimulateur === 'souscription' && (
                      <div className="border-t pt-4 mt-4">
                        <Button 
                          onClick={handleSouscrire}
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Souscrire à cette offre
                        </Button>
                        <p className="text-xs text-center mt-2 text-slate-500">
                          Finalisez votre souscription en quelques étapes simples
                        </p>
                      </div>
                    )}

                    {/* Mode ANNUEL - Pour les COOPER'S, Admins et Super Admins */}
                    {modeCalcul === 'annuel' && user && (user.role === 'cooper' || user.role === 'admin' || user.role === 'super_admin') && modeSimulateur === 'souscription' && (
                      <div className="border-t pt-4 mt-4">
                        <Button 
                          onClick={handleSouscrire}
                          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Créer une souscription annuelle pour un membre adhérent
                        </Button>
                        <p className="text-xs text-center mt-2 text-slate-500">
                          Sélectionnez ou créez un membre adhérent pour cette souscription annuelle
                        </p>
                      </div>
                    )}

                    {/* Mode NOMINATIVE - Pour les COOPER'S, Admins et Super Admins */}
                    {modeCalcul === 'nominative' && user && (user.role === 'cooper' || user.role === 'admin' || user.role === 'super_admin') && modeSimulateur === 'souscription' && (
                      <div className="border-t pt-4 mt-4">
                        <Button 
                          onClick={handleSouscrire}
                          className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Créer une souscription nominative pour un membre
                        </Button>
                        <p className="text-xs text-center mt-2 text-slate-500">
                          Sélectionnez ou créez un membre adhérent pour cette souscription nominative
                        </p>
                      </div>
                    )}
    </>
  );
}
